// core/data/more-links.ts — typed seed for the More hub page data.
//
// Faithfully ported from legacy src/scripts/data/more-links.js.
// Icons are inline SVG strings. Each row has either `href` (React Router
// history path, e.g. '/settings') or `soon: true` (decorative).
// `primary: true` marks the emphasized row.

import type { MoreGroup, QuickToggle } from '@/core/models/more';

// Inline SVG icon strings (verbatim from legacy).
const ICON = {
  settings:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"/></svg>',
  privacy:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/></svg>',
  access:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="5" r="1.6"/><path d="M5 8h14M12 8v6m0 0l-3 6m3-6l3 6"/></svg>',
  bell:        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>',
  council:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/><path d="m9 12 2 2 4-4"/></svg>',
  guidelines:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>',
  shield:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/><path d="M12 9v4"/><circle cx="12" cy="16" r="0.6" fill="currentColor"/></svg>',
  report:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 21V4h11l-1.5 4L15 12H4"/></svg>',
  premium:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 6 L11 14 L5 22 M13 6 L19 14 L13 22"/></svg>',
  money:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9"/><path d="M14.5 9c-.5-1-1.5-1.5-2.7-1.5-1.5 0-2.6.8-2.6 2 0 2.8 5.6 1.4 5.6 4.2 0 1.3-1.2 2.1-2.8 2.1"/></svg>',
  hub:         '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="3"/><circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/><circle cx="5" cy="18" r="2"/><circle cx="19" cy="18" r="2"/><path d="M7 7l3 3M17 7l-3 3M7 17l3-3M17 17l-3-3"/></svg>',
  communities: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="8" cy="9" r="2.6"/><circle cx="16" cy="9" r="2.6"/><path d="M3 19c0-2.5 2-4 5-4s5 1.5 5 4"/></svg>',
  bookmark:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4.5L5 21V4a1 1 0 0 1 1-1z"/></svg>',
  explore:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9"/><path d="m15 9-2 6-4 0 2-6z" fill="currentColor"/></svg>',
  video:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m10 9 6 3-6 3z" fill="currentColor"/></svg>',
  info:        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><circle cx="12" cy="8" r="0.6" fill="currentColor"/></svg>',
  help:        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.8.3-1.2.9-1.2 1.6v.5"/><circle cx="12" cy="17" r="0.6" fill="currentColor"/></svg>',
  keyboard:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10"/></svg>',
  legal:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 3h9l3 3v15H6z"/><path d="M9 9h6M9 13h6M9 17h4"/></svg>',
  feedback:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 10h8M8 13h5"/></svg>',
  support:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>',
};

export const seedMoreGroups: MoreGroup[] = [
  {
    id: 'settings',
    title: 'Settings & Privacy',
    rows: [
      { label: 'Settings and privacy', sub: 'Manage your account, security, and data', icon: ICON.settings, href: '/settings', primary: true },
      { label: 'Privacy & Security',    icon: ICON.privacy, href: '/settings/privacy' },
      { label: 'Accessibility',          icon: ICON.access,  href: '/settings/accessibility' },
      { label: 'Notification settings',  icon: ICON.bell,    href: '/settings/notifications' },
    ],
  },
  {
    id: 'safety',
    title: 'Safety & Moderation',
    rows: [
      { label: 'Council of Moderators', sub: 'See how moderation decisions get made', icon: ICON.council, href: '/council' },
      { label: 'Community Guidelines',  sub: 'The five-and-only-five categories',      icon: ICON.guidelines, href: '/policies/guidelines' },
      { label: 'Help with reporting',   sub: 'What the five categories mean and what happens next', icon: ICON.report, href: '/help/reporting' },
      { label: 'Trust & Safety',        sub: 'Trust scores, your controls, and the legal floor',    icon: ICON.shield, href: '/help/safety' },
      // No account needed, deliberately — see LegalFloorReportPage. Listed here
      // for members too, but the route a guest uses is linked from the policies.
      { label: 'Report illegal content', sub: 'Unlawful material, handled outside the Council',     icon: ICON.legal,  href: '/report-illegal' },
    ],
  },
  {
    id: 'creator',
    title: 'Creator Tools',
    rows: [
      { label: 'TruX Premium',  icon: ICON.premium, href: '/premium' },
      { label: 'Monetization',  icon: ICON.money,   soon: true },
      { label: "Creators' Hub", icon: ICON.hub,     href: '/creators-hub' },
    ],
  },
  {
    id: 'discover',
    title: 'Discover',
    rows: [
      { label: 'Communities', icon: ICON.communities, href: '/communities' },
      { label: 'Bookmarks',   icon: ICON.bookmark,    href: '/bookmarks' },
      { label: 'Explore',     icon: ICON.explore,     href: '/explore' },
      // Not 'soon' — /videos and /live both ship and both sit in the left rail.
      // Videos was a dead end that toasted "coming soon" about a page the member
      // could already reach in one click, and Live was missing from More entirely.
      { label: 'Videos',      icon: ICON.video,       href: '/videos' },
      { label: 'Live',        icon: ICON.video,       href: '/live' },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    rows: [
      { label: 'Feedback',           sub: 'Suggest features, report bugs, upvote ideas', icon: ICON.feedback, href: '/feedback' },
      { label: 'Support TruXSocial', sub: 'Independent, ad-free. Help keep it running', icon: ICON.support, href: '/support' },
      { label: 'Update Logs',       icon: ICON.info,     href: '/settings/about' },
      { label: 'Help Center',        icon: ICON.help,     soon: true },
      { label: 'Keyboard shortcuts', icon: ICON.keyboard, soon: true },
      { label: 'Terms & Policies',   sub: 'What we promise you, and what we ask of you', icon: ICON.legal, href: '/policies' },
    ],
  },
];

// Every quick toggle here must actually write a preference. "Autoplay media" and
// "Data saver — Use less data on cellular connections" used to sit alongside Reduce
// motion, styled identically and carrying no marker, while writing nothing but local
// state that was discarded on navigate. Under a heading reading "Quick settings"
// that is a promise; "Data saver" in particular is a promise about someone's phone
// bill. They are removed rather than labelled, because neither feature is planned.
export const seedQuickToggles: QuickToggle[] = [
  { id: 'reduce-motion',  label: 'Reduce motion',  sub: 'Minimize animations and transitions', functional: true },
];
